import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import adminApi, { Metrics } from '../../api/admin';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminMetrics: React.FC = () => {
  const { t } = useLanguage();
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi
      .getMetrics()
      .then(setMetrics)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-slate-500">{t.common.loading}</div>;
  if (!metrics) return null;

  const planData = Object.entries(metrics.subscribersByPlan).map(([plan, count]) => ({
    plan,
    count,
  }));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">
        {t.admin?.metrics?.title ?? 'Métriques SaaS'}
      </h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label={t.admin?.metrics?.mrr ?? 'MRR (TND)'}
          value={metrics.monthlyRecurringRevenue.toFixed(3)}
          tone="bg-green-50 text-green-700"
        />
        <KpiCard
          label={t.admin?.metrics?.arr ?? 'ARR (TND)'}
          value={metrics.annualRecurringRevenue.toFixed(3)}
          tone="bg-indigo-50 text-indigo-700"
        />
        <KpiCard
          label={t.admin?.metrics?.totalSubscribers ?? 'Abonnés actifs'}
          value={String(metrics.totalSubscribers)}
          tone="bg-blue-50 text-blue-700"
        />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">
          {t.admin?.metrics?.byPlan ?? 'Abonnés par plan'}
        </h2>
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart data={planData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="plan" stroke="#64748b" />
              <YAxis allowDecimals={false} stroke="#64748b" />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          label={t.admin?.metrics?.paidThisMonth ?? 'Factures payées'}
          value={String(metrics.invoicesPaidThisMonth)}
          tone="bg-green-50 text-green-700"
        />
        <KpiCard
          label={t.admin?.metrics?.pendingThisMonth ?? 'Factures en attente'}
          value={String(metrics.invoicesPendingThisMonth)}
          tone="bg-amber-50 text-amber-700"
        />
        <KpiCard
          label={t.admin?.metrics?.planChanges ?? 'Changements de plan (30 j)'}
          value={String(metrics.planChangesLast30Days)}
          tone="bg-purple-50 text-purple-700"
        />
      </div>
    </div>
  );
};

const KpiCard: React.FC<{ label: string; value: string; tone: string }> = ({
  label,
  value,
  tone,
}) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className={`inline-block px-3 py-1 rounded text-xs font-medium ${tone}`}>{label}</div>
    <div className="mt-3 text-3xl font-bold text-slate-900">{value}</div>
  </div>
);

export default AdminMetrics;
